// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useState } from "react";
import type {
  CockpitApi,
  CockpitIdentity,
  ServiceCredentialGrant,
  ServiceCredentialRequest,
} from "./api.js";

interface IssuanceRequest {
  readonly body: ServiceCredentialRequest;
  readonly idempotencyKey: string;
}

function restoreRequest(key: string): IssuanceRequest | null {
  const saved = sessionStorage.getItem(key);
  if (saved === null) return null;
  const value: unknown = JSON.parse(saved);
  if (
    typeof value !== "object" ||
    value === null ||
    !("idempotencyKey" in value) ||
    typeof value.idempotencyKey !== "string" ||
    !("body" in value) ||
    typeof value.body !== "object" ||
    value.body === null
  )
    throw new Error(
      "Saved issuance request is invalid. Keep it for operator recovery.",
    );
  const body = value.body;
  if (
    !("actorId" in body) ||
    typeof body.actorId !== "string" ||
    !("displayName" in body) ||
    typeof body.displayName !== "string" ||
    !("expiresAt" in body) ||
    typeof body.expiresAt !== "string" ||
    !("grants" in body) ||
    !Array.isArray(body.grants) ||
    !body.grants.every((grant: unknown) => typeof grant === "string")
  )
    throw new Error(
      "Saved issuance request is invalid. Keep it for operator recovery.",
    );
  return {
    idempotencyKey: value.idempotencyKey,
    body: {
      actorId: body.actorId,
      displayName: body.displayName,
      expiresAt: body.expiresAt,
      grants: body.grants,
    },
  };
}

export function ServiceCredentials({
  api,
  identity,
}: {
  readonly api: CockpitApi;
  readonly identity: CockpitIdentity;
}) {
  const storageKey = `diffclaw.service-issuance:${identity.organizationId}:${identity.actorId}`;
  const [initial] = useState(() => {
    try {
      return { request: restoreRequest(storageKey), error: null };
    } catch {
      return {
        request: null,
        error:
          "The saved issuance request could not be read. Recover it before creating another credential.",
      };
    }
  });
  const [request, setRequest] = useState<IssuanceRequest | null>(
    initial.request,
  );
  const [actorId, setActorId] = useState(
    initial.request?.body.actorId ?? crypto.randomUUID(),
  );
  const [displayName, setDisplayName] = useState(
    initial.request?.body.displayName ?? "",
  );
  const [expiresAt, setExpiresAt] = useState(
    initial.request?.body.expiresAt ??
      new Date(Date.now() + 7 * 86_400_000).toISOString(),
  );
  const [grants, setGrants] = useState(
    initial.request?.body.grants.join("\n") ?? "",
  );
  const [grant, setGrant] = useState<ServiceCredentialGrant | null>(null);
  const [revokeId, setRevokeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(initial.error);
  const [failed, setFailed] = useState(initial.error !== null);

  async function issue() {
    if (busy || initial.error !== null || grant !== null) return;
    setBusy(true);
    setFailed(false);
    let sent = request !== null;
    try {
      const next = request ?? {
        idempotencyKey: crypto.randomUUID(),
        body: {
          actorId,
          displayName: displayName.trim(),
          expiresAt: new Date(expiresAt).toISOString(),
          grants: grants.split(/\s+/u).filter(Boolean),
        },
      };
      if (request === null) {
        const expiry = Date.parse(next.body.expiresAt) - Date.now();
        if (
          !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(
            next.body.actorId,
          ) ||
          next.body.displayName.length === 0 ||
          expiry <= 0 ||
          expiry > 365 * 86_400_000 ||
          next.body.grants.length === 0 ||
          next.body.grants.length > 64 ||
          new Set(next.body.grants).size !== next.body.grants.length ||
          !next.body.grants.every((permission) =>
            /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/u.test(permission),
          )
        )
          throw new Error(
            "Enter a valid actor UUID, service name, future expiry within one year, and 1–64 distinct permission names",
          );
      }
      // Save only the command identity, before sending. A reload must not mint
      // another credential after a response is lost.
      sessionStorage.setItem(storageKey, JSON.stringify(next));
      setRequest(next);
      sent = true;
      setStatus("Issuing the requested service access…");
      const result = await api.issueServiceCredential(
        next.body,
        next.idempotencyKey,
      );
      setGrant(result);
      setRevokeId(result.credentialId);
      setStatus(
        result.token === undefined
          ? "Issuance recovered. The token cannot be retrieved again. Revoke this credential and issue a replacement."
          : "Credential issued. Download its token now and keep the credential ID for revocation. The token is lost when you leave or reload this page.",
      );
    } catch (error) {
      setFailed(true);
      setStatus(
        `${error instanceof Error ? error.message : "Issuance failed"}. ${sent ? "Recover the same request before issuing a replacement." : "Check the fields and browser storage before trying again."}`,
      );
    } finally {
      setBusy(false);
    }
  }

  function clearRequest() {
    sessionStorage.removeItem(storageKey);
    setGrant(null);
    setRequest(null);
    setActorId(crypto.randomUUID());
  }

  function prepareAnother() {
    try {
      clearRequest();
      setDisplayName("");
      setGrants("");
      setExpiresAt(new Date(Date.now() + 7 * 86_400_000).toISOString());
      setFailed(false);
      setStatus(
        "Ready to prepare another credential. Previously issued access remains active until revoked or expired.",
      );
    } catch {
      setFailed(true);
      setStatus(
        "The saved request could not be cleared. No new request was created.",
      );
    }
  }

  async function revoke(credentialId: string) {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await api.revokeServiceCredential(credentialId);
      if (grant?.credentialId === credentialId) {
        clearRequest();
      }
      setStatus(`Credential ${credentialId} revoked.`);
      setRevokeId("");
    } catch (error) {
      setFailed(true);
      setStatus(error instanceof Error ? error.message : "Revocation failed.");
    } finally {
      setBusy(false);
    }
  }

  function downloadToken() {
    if (grant?.token === undefined) return;
    const url = URL.createObjectURL(
      new Blob([grant.token], { type: "text/plain" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `diffclaw-service-${grant.credentialId}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Service access</h1>
          <p>
            An Organization owner can issue an expiring credential for
            unattended work.
          </p>
        </div>
      </header>
      <section className="panel onboarding-step">
        <div className="panel-head">
          <div>
            <h2>Issue a service credential</h2>
            <p>
              The listed permissions apply across this Organization. Review the
              grants and expiry before issuing access. Automatic review
              activation and GitHub publication consent are configured
              separately.
            </p>
          </div>
        </div>
        <form
          className="panel-pad stack"
          onSubmit={(event) => {
            event.preventDefault();
            void issue();
          }}
        >
          <fieldset
            className="stack service-access-fields"
            disabled={busy || request !== null || initial.error !== null}
          >
            <legend className="sr-only">Service credential details</legend>
            <label>
              Service name
              <input
                required
                maxLength={160}
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                }}
              />
            </label>
            <label>
              Expires at (UTC)
              <input
                required
                className="code-input"
                value={expiresAt}
                onChange={(event) => {
                  setExpiresAt(event.target.value);
                }}
              />
              <span className="field-help">
                Use an ISO timestamp, for example 2026-11-01T12:00:00.000Z. The
                maximum lifetime is one year.
              </span>
            </label>
            <label>
              Permission grants
              <textarea
                required
                className="code-input"
                rows={8}
                value={grants}
                onChange={(event) => {
                  setGrants(event.target.value);
                }}
              />
              <span className="field-help">
                Enter one exact permission per line from the operation’s
                deployment runbook.
              </span>
            </label>
            <details className="disclosure">
              <summary>Service actor identity</summary>
              <label>
                Service actor ID
                <input
                  required
                  className="code-input"
                  value={actorId}
                  onChange={(event) => {
                    setActorId(event.target.value);
                  }}
                />
              </label>
            </details>
          </fieldset>
          {grant === null ? (
            <button
              className="button primary"
              type="submit"
              disabled={busy || initial.error !== null}
            >
              {request === null
                ? "Issue credential"
                : "Recover issuance result"}
            </button>
          ) : (
            <div className="stack">
              <label>
                Credential ID
                <input
                  className="code-input"
                  readOnly
                  value={grant.credentialId}
                />
              </label>
              <label>
                Issued service actor ID
                <input
                  className="code-input"
                  readOnly
                  value={grant.actor.actorId}
                />
              </label>
              <p>Expires {grant.expiresAt}</p>
              <div className="button-row">
                {grant.token === undefined ? null : (
                  <button
                    className="button primary"
                    type="button"
                    onClick={downloadToken}
                  >
                    Download token
                  </button>
                )}
                <button
                  className="button secondary"
                  type="button"
                  disabled={busy}
                  onClick={() => void revoke(grant.credentialId)}
                >
                  Revoke issued credential
                </button>
                <button
                  className="button secondary"
                  type="button"
                  disabled={busy}
                  onClick={prepareAnother}
                >
                  Prepare another credential
                </button>
              </div>
            </div>
          )}
        </form>
      </section>
      <p role={failed ? "alert" : "status"}>{status}</p>
      <section className="panel onboarding-step">
        <div className="panel-head">
          <div>
            <h2>Revoke existing access</h2>
            <p>
              Revocation stops subsequent authenticated operations using this
              credential.
            </p>
          </div>
        </div>
        <form
          className="panel-pad stack"
          onSubmit={(event) => {
            event.preventDefault();
            void revoke(revokeId);
          }}
        >
          <label>
            Credential ID to revoke
            <input
              required
              className="code-input"
              value={revokeId}
              onChange={(event) => {
                setRevokeId(event.target.value);
              }}
            />
          </label>
          <button
            className="button secondary"
            type="submit"
            disabled={busy || revokeId.length === 0}
          >
            Revoke credential
          </button>
        </form>
      </section>
    </div>
  );
}
